/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useCallback, useState } from "react";

import {
  CommandBar,
  DefaultButton,
  FocusTrapZone,
  Layer,
  Overlay,
  Panel,
  PanelType,
  Popup,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";

import { OperationStatus } from "Constants/OperationStatus";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";

import { AccessControlRole } from "./AccessControlRole";
import {
  commandBarStyles,
  containerStyles,
  deleteButtonStyles,
  panelHeaderStyles,
  popupContentStyles,
  popupHorizontalGapStackTokens,
  popupStyles,
  popupTitleStyles,
} from "./AccessControlRoles.styles";
import AccessControlRolesForm from "./AccessControlRolesForm";
import { spinnerStyles } from "./AccessControlRolesForm.styles";
import { IAccessControlRolesProps } from "./AccessControlRolesProps";

const AccessControlRoles = (props: IAccessControlRolesProps): React.ReactElement => {
  const { isLoadingStatus, selectedUser, roles, tenant, actions, technologyFocusAreas, geoData } =
    props;

  const isLoading = isLoadingStatus === OperationStatus.Pending;

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(0);
  const [editingUserRole, setEditingUserRole] = useState<IUserRole>();

  const openPanel = () => {
    setEditingUserRole(undefined);
    setIsPanelOpen(true);
  };

  const dismissPanel = () => {
    setIsPanelOpen(false);
  };

  const openConfirmationModal = (roleId: number) => {
    setIsConfirmationOpen(true);
    setSelectedRoleId(roleId);
  };

  const dismissConfirmationModal = () => {
    setIsConfirmationOpen(false);
  };

  const saveRole = (role: IUserRole): void => {
    role.technologyAreas = role.technologyAreas.map((x) => {
      return x.split("__")[0];
    });

    if (!editingUserRole) {
      actions.onCreateUserRole(tenant, role);
    } else {
      actions.onUpdateUserRole(tenant, role);
    }

    dismissPanel();
  };

  const editRole = (role: IUserRole): void => {
    setIsPanelOpen(true);
    setEditingUserRole(role);
  };

  const deleteRole = () => {
    actions.onDeleteUserRole(selectedRoleId);
    setIsConfirmationOpen(false);
  };

  const onRenderHeaderContent = useCallback(
    () => <Text styles={panelHeaderStyles}>Choose Role</Text>,
    []
  );

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  const commandBarItems = [
    {
      key: "addNewRole",
      text: "Add New Role",
      iconProps: { iconName: "Add" },
      onClick: (): void => {
        openPanel();
      },
    },
  ];

  return (
    <>
      <CommandBar items={commandBarItems} ariaLabel="Add New Role" styles={commandBarStyles} />
      {roles.length > 0 ? (
        <Stack styles={containerStyles}>
          {roles.map((r) => {
            return (
              <AccessControlRole
                key={r.id}
                role={r}
                actions={{
                  editRole: editRole,
                  confirmDeletingRole: openConfirmationModal,
                }}
              />
            );
          })}
        </Stack>
      ) : null}

      <Panel
        isOpen={isPanelOpen}
        onDismiss={dismissPanel}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
        onRenderHeader={onRenderHeaderContent}
        isFooterAtBottom={true}
      >
        <AccessControlRolesForm
          tenant={tenant}
          selectedUserId={selectedUser?.id}
          selectedUserRole={editingUserRole}
          technologyFocusAreas={technologyFocusAreas}
          geoData={geoData}
          actions={{
            saveRole,
            setIsPanelOpen: dismissPanel,
          }}
        />
      </Panel>

      {isConfirmationOpen && (
        <Layer>
          <Popup
            className={popupStyles.root}
            role="dialog"
            aria-modal="true"
            onDismiss={dismissConfirmationModal}
          >
            <Overlay onClick={dismissConfirmationModal} />
            <FocusTrapZone>
              <div role="document" className={popupStyles.content}>
                <Stack tokens={popupHorizontalGapStackTokens}>
                  <Stack.Item>
                    <Text styles={popupTitleStyles}>Delete Role</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text styles={popupContentStyles}>
                      This action in irreversible. Are you sure?
                    </Text>
                  </Stack.Item>
                  <Stack horizontal horizontalAlign="end">
                    <DefaultButton onClick={deleteRole} styles={deleteButtonStyles}>
                      Delete
                    </DefaultButton>
                    <PrimaryButton onClick={dismissConfirmationModal}>Cancel</PrimaryButton>
                  </Stack>
                </Stack>
              </div>
            </FocusTrapZone>
          </Popup>
        </Layer>
      )}
    </>
  );
};

export default AccessControlRoles;
